/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import {
  withStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { DispatchDragNDropTypes, DispatchOperationType, DispatchStatusNumber } from '~/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import PF2MEquipIcon from '~/components/PF2MEquipIcon';
import PF2MDispatchIcon from '~/components/PF2MDispatchIcon';
import { setSelectedTruckOrigin, setSelectedLoadOrigin } from '~/store/dispatch/actions';
import { getCodeFromStatus, toCodesMapByKey } from '~/utils/codes';

const getWarningType = (truckOrigin, loadAllocations, isGenericRow) => {
  if (isGenericRow) return 'standardWarning';
  const expectedLoadAllocation = loadAllocations
    .find(r => r.id === truckOrigin.load_allocation_id);

  if (expectedLoadAllocation
    && truckOrigin.current_excavator_id !== expectedLoadAllocation.equip_id) {
    return 'standardWarning';
  }
  return null;
};

const OriginRow = ({
  onTruckDrop,
  origin,
  leftLane,
  rightLane,
  isGenericRow = false,
  loadAllocations,
  openTruckModal,
  openExcavatorModal,
  onTruckContextMenu,
  onLoadContextMenu,
}) => {
  const order = [
    DispatchOperationType.LOAD,
    DispatchOperationType.UNLOAD,
    DispatchOperationType.LOAD_MANEUVER,
    DispatchOperationType.UNLOAD_MANEUVER,
    DispatchOperationType.LOAD_QUEUE,
    DispatchOperationType.UNLOAD_QUEUE,
    DispatchOperationType.LOAD_TRAFFIC,
    DispatchOperationType.UNLOAD_TRAFFIC,
    DispatchOperationType.EMPTY_STOP,
    DispatchOperationType.RELEASED_EQUIPMENT,
  ];

  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const codes = useSelector(state => state.manager.codes);
  const codesByKey = useMemo(() => toCodesMapByKey(codes), [codes]);
  const operators = useSelector(state => state.manager.operators);
  const syncStatus = useSelector(state => state.dispatch.syncStatus);

  const leftLaneSorted = useMemo(() => leftLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [leftLane, order]);

  const leftLaneDurations = leftLaneSorted.map((left) => {
    const matchingStatuses = equipmentStatuses.filter(
      status => status.id_equip === left.equip_id,
    );

    const durations = matchingStatuses.flatMap(status => status.duration);

    const mappedStatuses = matchingStatuses.map((status) => {
      const foundCode = getCodeFromStatus(codesByKey, status);
      const foundOperator = operators.find(op => op.id_operator === status.id_operator);
      const foundSyncStatus = syncStatus.find(s => s.equip_id === status.id_equip);

      return {
        ...status,
        codeId: foundCode ? foundCode.id : translate('validation:CodeNotFound'),
        codeName: foundCode ? foundCode.name : translate('validation:CodeNotFound'),
        operatorName: foundOperator ? foundOperator.name : translate('validation:OperatorNotFound'),
        timestampSyncRequest: foundSyncStatus ? foundSyncStatus.timestamp_sync_request : null,
        originalStatus: foundCode ? foundCode.code_type : null,
      };
    });

    const codesId = mappedStatuses.map(status => status.codeId);
    const codesNames = mappedStatuses.map(status => status.codeName);
    const operatorsNames = mappedStatuses.map(status => status.operatorName);
    const curStatus = mappedStatuses.map(status => status.originalStatus);
    const syncStatusAll = mappedStatuses.map(status => status.timestampSyncRequest);

    return {
      ...left,
      durations,
      codes_id: codesId,
      status:
        DispatchStatusNumber[curStatus] === undefined
          ? left.status : DispatchStatusNumber[curStatus],
      codes_name: codesNames,
      operators_name: operatorsNames,
      timestamp_sync_request: syncStatusAll,
    };
  });

  const rightLaneSorted = useMemo(() => rightLane.sort(
    (a, b) => order.indexOf(a.operation_type) - order.indexOf(b.operation_type),
  ), [rightLane, order]);

  const rightLaneDurations = useMemo(() => rightLaneSorted.map((right) => {
    const matchingStatuses = equipmentStatuses.filter(
      status => status.id_equip === right.equip_id,
    );

    const durations = matchingStatuses.flatMap(status => status.duration);

    const mappedStatuses = matchingStatuses.map((status) => {
      const foundCode = getCodeFromStatus(codesByKey, status);
      const foundOperator = operators.find(op => op.id_operator === status.id_operator);
      const foundSyncStatus = syncStatus.find(s => s.equip_id === status.id_equip);

      return {
        ...status,
        codeId: foundCode ? foundCode.id : translate('common:CodeNotFound'),
        codeName: foundCode ? foundCode.name : translate('common:CodeNotFound'),
        operatorName: foundOperator ? foundOperator.name : translate('common:OperatorNotFound'),
        timestampSyncRequest: foundSyncStatus ? foundSyncStatus.timestamp_sync_request : null,
        originalStatus: foundCode ? foundCode.code_type : null,
      };
    });

    const codesId = mappedStatuses.map(status => status.codeId);
    const codesNames = mappedStatuses.map(status => status.codeName);
    const operatorsNames = mappedStatuses.map(status => status.operatorName);
    const curStatus = mappedStatuses.map(status => status.originalStatus);
    const syncStatusAll = mappedStatuses.map(status => status.timestampSyncRequest);

    return {
      ...right,
      durations,
      codes_id: codesId,
      status:
        DispatchStatusNumber[curStatus] === undefined
          ? right.status : DispatchStatusNumber[curStatus],
      codes_name: codesNames,
      operators_name: operatorsNames,
      timestamp_sync_request: syncStatusAll,
    };
  }), [rightLaneSorted, equipmentStatuses, codesByKey, operators, syncStatus, translate]);

  const [{ dragItem }, truckDrop] = useDrop({
    accept: origin.id === null ? []
      : [DispatchDragNDropTypes.ORIGIN_TRUCK, DispatchDragNDropTypes.POOL_TRUCK],

    drop: (monitor) => {
      if (origin.id === null) return;

      if (origin.id !== null
        && monitor.obj.current_excavator_id !== origin.equip_id) {
        onTruckDrop(monitor.obj, origin.id);
      }
    },

    collect: monitor => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
      dragItem: monitor.getItem(),
    }),
  });

  return (
    <div
      key={origin.id}
      style={{
        display: 'flex',
        width: '100%',
        height: 'fit-content',
        backgroundColor: '#dedede',
      }}
      ref={truckDrop}
    >
      <div
        style={{
          width: '50%',
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: '20px',
          border: '1px solid #CDCDCD',
          paddingBlock: 5,
        }}
      >
        <PF2MDispatchIcon
          iconType="stack"
          customStyles={{
            marginRight: '10px',
            cursor: 'default',
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            leftLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={`${r.current_destination_point_name}`}
                item={r}
                itemType={DispatchDragNDropTypes.ORIGIN_TRUCK}
                useOperationType={
                  !((r.operation_type === 11 || r.operation_type === 24))
                }
                customStyles={{
                  gap: 5,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                warningType={getWarningType(r, loadAllocations, isGenericRow)}
                onClick={() => {
                  dispatch(setSelectedTruckOrigin(r));
                  openTruckModal(r);
                }}
                onContextMenu={onTruckContextMenu}
                tooltipData={r}
              />
            ))
          }
        </div>
      </div>
      <div
        style={{
          gap: '20px',
          width: '50%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse',
          border: '1px solid #CDCDCD',
          paddingBlock: 5,
        }}
      >
        <PF2MEquipIcon
          item={origin}
          itemType="load"
          customStyles={{
            marginRight: 15,
          }}
          onContextMenu={onLoadContextMenu}
          onClick={() => {
            if (!isGenericRow) {
              dispatch(setSelectedLoadOrigin(origin));
              openExcavatorModal(origin);
            }
          }}
          warningType={
            (!origin.priority && !isGenericRow ? 'standardWarning' : null)
          }
          tooltipData={origin}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {
            rightLaneDurations.map(r => (
              <PF2MEquipIcon
                key={r.equip_id}
                extraInfo={`${r.load_allocation_excavator_name}`}
                item={r}
                itemType={DispatchDragNDropTypes.ORIGIN_TRUCK}
                useOperationType={
                  (r.operation_type === 11 || r.operation_type === 24)
                }
                customStyles={{
                  gap: 5,
                  opacity: (dragItem && dragItem.obj?.equip_id === r.equip_id) ? 0 : 1,
                }}
                onContextMenu={onTruckContextMenu}
                warningType={getWarningType(r, loadAllocations, isGenericRow)}
                onClick={() => {
                  dispatch(setSelectedTruckOrigin(r));
                  openTruckModal(r);
                }}
                tooltipData={r}
              />
            ))
          }
        </div>
      </div>
    </div>
  );
};

OriginRow.propTypes = {
  onTruckContextMenu: PropTypes.func,
  onTruckDrop: PropTypes.func,
  origin: PropTypes.object,
  leftLane: PropTypes.array,
  rightLane: PropTypes.array,
  isGenericRow: PropTypes.bool,
  loadAllocations: PropTypes.array,
  openTruckModal: PropTypes.func,
  openExcavatorModal: PropTypes.func,
  onLoadContextMenu: PropTypes.func,
};

OriginRow.defaultProps = {
  onTruckDrop: () => null,
  origin: {},
  leftLane: [],
  rightLane: [],
  isGenericRow: false,
  loadAllocations: [],
  openTruckModal: () => null,
  openExcavatorModal: () => null,
  onTruckContextMenu: null,
  onLoadContextMenu: null,
};

export default withStyles(styles)(OriginRow);
